import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';

import { selectAppointmentsWithLocationByActorId } from './appointmentSlice';
import { selectActorAvailabilities } from './availabilitySlice';
import { goToActorCalendar } from './calendarSlice';
import { createLocation, deleteLocation, getLocations, getLocationsWithSlots, updateLocation } from 'FRONTEND/utils/backendApi/locationApi';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { getDeserializedState } from 'FRONTEND/utils/serialization';

const REDUCER_NAME = 'location';

const locationAdapter = createEntityAdapter({
  selectId: (location) => location.id,
});

export const listLocations = createAsyncThunk(`${REDUCER_NAME}/listLocations`, async() => {
  return {
    locationsList: await getLocations(),
  };
});

export const listLocationsWithSlot = createAsyncThunk(`${REDUCER_NAME}/listLocationsWithSlot`, async({ ignoreManagementRight }) => {
  const locations = await getLocationsWithSlots(ignoreManagementRight);
  return { locationsWithSlotsIds: locations.map((loc) => loc.id) };
},
);

export const addLocation = createAsyncThunk(`${REDUCER_NAME}/addLocation`, async({ description, address }, { getState }) => {
  return {
    location: await createLocation({ description, address, resourceId: getState().calendar.actorId }),
  };
});

export const removeLocation = createAsyncThunk(`${REDUCER_NAME}/removeLocation`, async({ locationId }, { getState }) => {
  await deleteLocation({ locationId });
  return locationId;
});

export const editLocation = createAsyncThunk(`${REDUCER_NAME}/editLocation`, async(location) => {
  const updatedLocation = await updateLocation({ location });
  return updatedLocation;
},
);
const storedState = getDeserializedState()?.[REDUCER_NAME];

const initialState = {
  ...locationAdapter.getInitialState(),
  error: null,
  locationsWithSlotsIds: [],
  checkedLocationsList: [],
  ...storedState,
};

const locationSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    updateCheckedLocationsList(state, { payload: { checkedLocationsList } }) {
      state.checkedLocationsList = checkedLocationsList;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listLocations.fulfilled, (state, { payload: { locationsList } }) => {
      locationAdapter.setAll(state, locationsList);
    });
    builder.addCase(listLocationsWithSlot.fulfilled, (state, { payload: { locationsWithSlotsIds } }) => {
      state.locationsWithSlotsIds = locationsWithSlotsIds;
    });
    builder.addCase(addLocation.fulfilled, (state, { payload: { location } }) => {
      locationAdapter.addOne(state, location);
      // Auto-select the new location
      if (!state.checkedLocationsList.includes(location.id)) {
        state.checkedLocationsList.push(location.id);
      }
    });
    builder.addCase(removeLocation.fulfilled, (state, { payload: locationId }) => {
      locationAdapter.removeOne(state, locationId);
    });
    builder.addCase(editLocation.fulfilled, (state, { payload }) => {
      locationAdapter.updateOne(state, { id: payload.id, changes: payload });
    });
    builder.addCase(goToActorCalendar, (state, { payload }) => {
      state.checkedLocationsList = [];
    });
  },
});

export const {
  selectById: selectLocationById,
  selectAll: selectAllLocations,
} = locationAdapter.getSelectors((state) => state.location);

export const selectResourceLocations = createSelector(
  selectAllLocations,
  (_, resourceId) => resourceId,
  (locationList, resourceId) => locationList.filter((location) => location.station_id === resourceId),
);

export const selectLocationsWithSlots = createSelector(
  selectAllLocations,
  (state) => state.location.locationsWithSlotsIds,
  (locationList, locationsWithSlotsIds) => locationList.filter((location) => locationsWithSlotsIds.includes(location.id)),
);

export const selectHasLocationAndServiceRights = createSelector(
  (state) => state.user.rights,
  (rights) => {
    const requiredRights = [RIGHTS.LOCATIONS_LIST, RIGHTS.SERVICES_LIST];
    return requiredRights.every((right) => rights.includes(right));
  },
);

export const selectAppoinmentAndAvailabilityLocations = createSelector(
  (state) => selectAppointmentsWithLocationByActorId(state, state.calendar.actorId || state.expert.checkedList),
  (state) => selectActorAvailabilities(state, state.calendar.actorId || state.expert.checkedList),
  (appointmentList, availabilities) => {
    const locationMap = new Map();
    appointmentList.forEach((appointment) => {
      if (appointment.location && appointment.location.id) {
        locationMap.set(appointment.location.id, appointment.location);
      }
    });
    availabilities.forEach((availability) => {
      if (availability.location && availability.location.id) {
        locationMap.set(availability.location.id, availability.location);
      }
    });
    return Array.from(locationMap.values());
  },
);

export const { updateCheckedLocationsList } = locationSlice.actions;
export default locationSlice.reducer;
